import { Link } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import Seo from "@/components/feature/Seo";
import { characters, relationships } from "@/mocks/characters";

const jsonLd = [
  {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: "继母与继姐漫画人物关系详解",
    description:
      "继母与继姐漫画人物关系全梳理：主角与继母、主角与继姐、继母与继姐的关系图谱与演变脉络详解。",
    url: "/relationships",
    inLanguage: "zh-CN",
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "首页", item: "/" },
      { "@type": "ListItem", position: 2, name: "人物关系", item: "/relationships" },
    ],
  },
];

const colorMap: Record<string, { chip: string; dot: string; ring: string }> = {
  primary: { chip: "bg-primary-100 text-primary-700", dot: "bg-primary-500", ring: "ring-primary-200" },
  accent: { chip: "bg-accent-100 text-accent-700", dot: "bg-accent-500", ring: "ring-accent-200" },
  secondary: { chip: "bg-secondary-100 text-secondary-700", dot: "bg-secondary-500", ring: "ring-secondary-200" },
};

const timeline = [
  { phase: "相遇与试探", desc: "父亲再婚，主角初次踏入新家庭。三人各自带着预设与不安，关系小心翼翼地开始。", point: "关系起点" },
  { phase: "误会与碰撞", desc: "性格差异与过往心结让相处充满摩擦，尤其是主角与继姐之间，误解一次次升级。", point: "矛盾积累" },
  { phase: "破冰与靠近", desc: "一次次的坦诚与共同经历，让三人开始放下防备，读到彼此话语背后的用心。", point: "关系转折" },
  { phase: "接纳与守望", desc: "从「新家人」真正成为「家人」，三人建立起相互信任与守护的稳定关系。", point: "关系成熟" },
];

export default function Relationships() {
  return (
    <Layout>
      <Seo
        title="继母与继姐漫画人物关系 - 主角/继母/继姐关系网图解"
        description="继母与继姐漫画人物关系全梳理：主角、继母角色、继姐角色三段关系的关系图谱、详解与演变脉络，理清人物关系网看这一篇。"
        keywords="继母与继姐漫画人物关系,继母与继姐漫画关系网,继母与继姐漫画主角,继母与继姐漫画继母角色,继母与继姐漫画继姐角色"
        jsonLd={jsonLd}
      />
      {/* 页头 */}
      <section className="bg-gradient-to-b from-background-100 to-background-50 pt-24 md:pt-32">
        <div className="mx-auto max-w-6xl px-4 pb-10 md:px-6 md:pb-14">
          <nav aria-label="面包屑" className="flex items-center gap-2 text-xs text-foreground-500">
            <Link to="/" className="transition-colors hover:text-primary-700">首页</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-foreground-700">人物关系</span>
          </nav>
          <h1 className="mt-4 font-heading text-3xl font-black text-foreground-950 md:text-4xl">
            继母与继姐漫画 · 人物关系详解
          </h1>
          <p className="mt-4 max-w-2xl text-sm leading-7 text-foreground-600 md:text-base md:leading-8">
            主角、继母、继姐三人组成的人物关系网，是理解整部作品的关键。这里从关系图谱、关系详解到演变脉络，
            一次为你梳理清楚。
          </p>
        </div>
      </section>

      {/* 关系图谱 */}
      <section className="mx-auto max-w-6xl px-4 pb-12 md:px-6 md:pb-16">
        <div className="rounded-lg border border-background-200 bg-background-100 p-6 md:p-10">
          <h2 className="text-center font-heading text-xl font-bold text-foreground-950 md:text-2xl">
            核心人物关系图谱
          </h2>
          <p className="mx-auto mt-2 max-w-xl text-center text-sm text-foreground-600">
            三位核心人物彼此牵动，共同构成完整的人物关系网。
          </p>

          <div className="mt-8 grid gap-4 md:grid-cols-3 md:gap-6">
            {characters.map((c) => {
              const cc = colorMap[c.color];
              return (
                <div
                  key={c.id}
                  className="flex flex-col items-center rounded-lg bg-background-50 p-6 text-center ring-1 ring-background-200"
                >
                  <img
                    src={c.image}
                    alt={`继母与继姐漫画${c.role}${c.name}关系图节点`}
                    title={`${c.name} ${c.role}`}
                    className={`h-20 w-20 rounded-full object-top object-cover ring-4 ${cc.ring}`}
                  />
                  <h3 className="mt-3 font-heading text-lg font-bold text-foreground-950">{c.name}</h3>
                  <span className={`mt-1 rounded-md px-2.5 py-0.5 text-xs font-medium ${cc.chip}`}>
                    {c.role}
                  </span>
                  <p className="mt-3 text-xs leading-5 text-foreground-600">{c.tagline}</p>
                </div>
              );
            })}
          </div>

          {/* 连接说明 */}
          <div className="mt-6 grid gap-3 rounded-lg border border-background-200 bg-background-50 p-5 sm:grid-cols-3">
            {[
              { i: "ri-arrow-right-up-line", t: "主角 × 继母", d: "渐进式信任" },
              { i: "ri-arrow-right-down-line", t: "主角 × 继姐", d: "破冰式和解" },
              { i: "ri-arrow-right-line", t: "继母 × 继姐", d: "母女式依偎" },
            ].map((n) => (
              <div key={n.t} className="flex items-center gap-3">
                <span className="grid h-9 w-9 shrink-0 items-center justify-center rounded-full bg-secondary-100 text-secondary-700">
                  <i className={n.i}></i>
                </span>
                <div>
                  <p className="text-sm font-medium text-foreground-950">{n.t}</p>
                  <p className="text-xs text-foreground-500">{n.d}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 关系详解 */}
      <section className="bg-background-100 py-12 md:py-16">
        <div className="mx-auto max-w-6xl px-4 md:px-6">
          <div className="text-center">
            <p className="text-xs font-semibold tracking-[0.3em] text-primary-600">RELATIONSHIP DETAILS</p>
            <h2 className="mt-3 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
              三段关系的温度
            </h2>
            <p className="mx-auto mt-3 max-w-xl text-sm leading-7 text-foreground-600">
              每一段关系都有各自的节奏与表达方式。
            </p>
          </div>

          <div className="mt-8 grid gap-4 md:grid-cols-3">
            {relationships.map((r) => (
              <article
                key={r.pair}
                className="rounded-lg border border-background-200 bg-background-50 p-6 transition-transform duration-300 hover:-translate-y-1"
              >
                <span className="text-xs font-semibold text-primary-600">{r.tone}</span>
                <h3 className="mt-2 font-heading text-lg font-bold text-foreground-950">{r.pair}</h3>
                <p className="mt-1 text-sm font-medium text-secondary-700">{r.title}</p>
                <p className="mt-3 text-sm leading-7 text-foreground-700">{r.description}</p>
                <p className="mt-4 inline-flex items-center gap-1.5 rounded-md bg-secondary-100 px-3 py-1.5 text-xs font-medium text-secondary-800">
                  <span className="h-1.5 w-1.5 rounded-full bg-secondary-500"></span>
                  {r.status}
                </p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* 关系演变时间线 */}
      <section className="mx-auto max-w-4xl px-4 py-14 md:px-6 md:py-20">
        <div className="text-center">
          <p className="text-xs font-semibold tracking-[0.3em] text-primary-600">EVOLUTION</p>
          <h2 className="mt-3 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
            关系网如何一步步建立
          </h2>
          <p className="mx-auto mt-3 max-w-xl text-sm leading-7 text-foreground-600">
            从最初的试探，到最终成为真正的一家人，关系的建立经历了一段真实的旅程。
          </p>
        </div>

        <ol className="mt-10 space-y-8">
          {timeline.map((t, i) => (
            <li key={t.phase} className="relative flex gap-5 md:gap-8">
              <div className="flex flex-col items-center">
                <span className="grid h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary-500 text-sm font-bold text-background-50">
                  {i + 1}
                </span>
                {i < timeline.length - 1 && <span className="mt-2 w-px flex-1 bg-background-200"></span>}
              </div>
              <div className="pb-2">
                <span className="rounded-md bg-accent-100 px-2.5 py-1 text-xs font-medium text-accent-700">
                  {t.point}
                </span>
                <h3 className="mt-3 font-heading text-lg font-bold text-foreground-950">{t.phase}</h3>
                <p className="mt-2 text-sm leading-7 text-foreground-700">{t.desc}</p>
              </div>
            </li>
          ))}
        </ol>
      </section>

      {/* 底部导航 */}
      <section className="mx-auto max-w-6xl px-4 pb-12 md:px-6">
        <div className="flex flex-col items-start justify-between gap-4 rounded-lg bg-background-100 p-6 sm:flex-row sm:items-center md:p-8">
          <div>
            <h3 className="font-heading text-lg font-bold text-foreground-950">想深入了解每一位角色？</h3>
            <p className="mt-2 text-sm text-foreground-600">
              从人物关系回到角色设定，看看主角、继母、继姐各自的背景与成长。
            </p>
          </div>
          <Link
            to="/characters"
            className="inline-flex items-center gap-1 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            查看角色详解 <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </section>
    </Layout>
  );
}